import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Search, ChevronLeft, Mail, MailOpen,
  AlertCircle, User, Clock, CheckCircle, RefreshCw,
  Send, Filter, Inbox, Archive, ScrollText, ChevronRight
} from 'lucide-react';
import '../styles/Admin.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMessages = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [reportThreads, setReportThreads] = useState({ total_unread: 0, cases: [] });

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter === 'unread') params.append('unread_only', 'true');
      params.append('limit', '50');

      const response = await fetch(`${API_URL}/api/admin/messages?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied');
        }
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      setMessages(data.messages || []);
      setStats({ total: data.total || 0, unread: data.unread_count || 0 });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Poll unread clinical-report-thread questions every 30s for live count.
  const fetchReportThreads = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/clinical-report-threads/unread`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setReportThreads({
        total_unread: data.total_unread || 0,
        cases: data.cases || [],
      });
    } catch (e) {
      // Non-fatal — pill simply won't show.
    }
  }, [token]);

  useEffect(() => {
    fetchReportThreads();
    const id = setInterval(fetchReportThreads, 30000);
    return () => clearInterval(id);
  }, [fetchReportThreads]);

  const markAsRead = async (messageId) => {
    try {
      await fetch(`${API_URL}/api/admin/messages/${messageId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Update local state
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      ));
      setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    if (!message.is_read) {
      markAsRead(message.id);
    }
  };

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <AlertCircle size={48} />
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/admin')} className="admin-btn primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-container">
        <div className="admin-header">
          <div className="admin-header-content">
            <button onClick={() => navigate('/admin')} className="back-btn">
              <ChevronLeft size={20} />
              Back
            </button>
            <h1>Message Center</h1>
            <p className="admin-subtitle">
              Patient messages and inquiries
            </p>
          </div>
          <div className="admin-header-actions">
            <button onClick={fetchMessages} className="admin-btn secondary">
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>

      {/* Stats Bar */}
      <div className="message-stats-bar">
        <div className="stat-pill">
          <Inbox size={16} />
          <span>{stats.total} Total</span>
        </div>
        <div className={`stat-pill ${stats.unread > 0 ? 'highlight' : ''}`}>
          <Mail size={16} />
          <span>{stats.unread} Unread</span>
        </div>
        <div
          className={`stat-pill ${reportThreads.total_unread > 0 ? 'highlight-gold' : ''}`}
          data-testid="report-thread-unread-pill"
          title="Unread clinical-report Q&A from patients"
        >
          <ScrollText size={16} />
          <span>{reportThreads.total_unread} Report Q&amp;A</span>
        </div>
      </div>

      {/* Live unread report-question stream — surfaces patient questions in real time */}
      {reportThreads.total_unread > 0 && (
        <div className="report-thread-panel" data-testid="report-thread-panel">
          <div className="report-thread-panel-head">
            <h3>
              <ScrollText size={18} /> Patient Report Questions
              <span className="report-thread-pill">{reportThreads.total_unread}</span>
            </h3>
            <button
              type="button"
              className="report-thread-refresh"
              onClick={fetchReportThreads}
              data-testid="report-thread-refresh-btn"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
          <p className="report-thread-help">
            New questions from patients on their released clinical reports. Click any
            row to open the case.
          </p>
          <ul className="report-thread-list">
            {reportThreads.cases.map((c) => (
              <li
                key={c.case_id}
                className="report-thread-row"
                onClick={() => navigate(`/admin/intakes/${c.case_id}`)}
                data-testid={`report-thread-row-${c.case_id}`}
              >
                <div className="report-thread-row-meta">
                  <span className="report-thread-case-num">
                    {c.case_number || c.case_id.slice(0, 8)}
                  </span>
                  <span className="report-thread-author">{c.latest_author_name || 'Patient'}</span>
                  <span className="report-thread-time">{formatTime(c.latest_at)}</span>
                  <span className="report-thread-count-chip">{c.count}</span>
                </div>
                <div className="report-thread-row-preview">{c.latest_preview}</div>
                <ChevronRight size={16} className="report-thread-arrow" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="message-filters">
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Messages
        </button>
        <button 
          className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread {stats.unread > 0 && <span className="badge">{stats.unread}</span>}
        </button>
      </div>

      {/* Messages Layout */}
      <div className="messages-layout">
        {/* Message List */}
        <div className="message-list-panel">
          {loading ? (
            <div className="admin-loading">
              <RefreshCw className="spin" size={24} />
              <p>Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="admin-empty">
              <MessageSquare size={48} />
              <h3>No messages</h3>
              <p>{filter === 'unread' ? 'All caught up!' : 'No messages from patients yet'}</p>
            </div>
          ) : (
            <div className="message-list">
              {messages.map((message) => (
                <div 
                  key={message.id}
                  className={`message-list-item ${!message.is_read ? 'unread' : ''} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                  onClick={() => handleSelectMessage(message)}
                  data-testid={`message-item-${message.id}`}
                >
                  <div className="message-indicator">
                    {message.is_read ? <MailOpen size={18} /> : <Mail size={18} />}
                  </div>
                  <div className="message-preview">
                    <div className="message-header-row">
                      <span className="message-sender">{message.sender_name || 'Patient'}</span>
                      <span className="message-time">{formatTime(message.created_at)}</span>
                    </div>
                    <div className="message-subject">{message.subject || 'No subject'}</div>
                    <div className="message-snippet">
                      {(message.content || '').substring(0, 80)}...
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Message Detail */}
        <div className="message-detail-panel">
          {selectedMessage ? (
            <div className="message-detail">
              <div className="message-detail-header">
                <div className="sender-info">
                  <div className="sender-avatar">
                    <User size={24} />
                  </div>
                  <div className="sender-details">
                    <h3>{selectedMessage.sender_name || 'Patient'}</h3>
                    <span className="sender-email">{selectedMessage.sender_email}</span>
                  </div>
                </div>
                <div className="message-meta">
                  <span className="message-date">
                    <Clock size={14} />
                    {formatDateTime(selectedMessage.created_at)}
                  </span>
                  {selectedMessage.is_read && (
                    <span className="read-status">
                      <CheckCircle size={14} />
                      Read
                    </span>
                  )}
                </div>
              </div>

              <div className="message-subject-line">
                <strong>Subject:</strong> {selectedMessage.subject || 'No subject'}
              </div>

              <div className="message-body">
                {selectedMessage.content}
              </div>

              {selectedMessage.case_id && (
                <div className="message-context">
                  <span>Related to Case: </span>
                  <a href={`/admin/intakes/${selectedMessage.case_id}`}>
                    View Case
                  </a>
                </div>
              )}

              <div className="message-actions">
                <button 
                  className="admin-btn primary"
                  onClick={() => window.location.href = `mailto:${selectedMessage.sender_email}?subject=Re: ${selectedMessage.subject || ''}`}
                >
                  <Send size={16} />
                  Reply via Email
                </button>
              </div>
            </div>
          ) : (
            <div className="message-detail-empty">
              <MessageSquare size={64} />
              <h3>Select a message</h3>
              <p>Choose a message from the list to view its contents</p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

// Helper functions
const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return date.toLocaleDateString('en-US', { weekday: 'short' });
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export default AdminMessages;
